import type { PlanetPosition, VargaChart, VargaId, VargaPlacement } from '@astrally/shared';
import { norm360 } from '../math/angles.js';
import { rashiName, signName } from '../core/zodiac.js';

/**
 * Divisional charts. D1/D9/D10 implemented; the registry pattern makes
 * additional vargas (D2, D3, D7, D12, D30…) a matter of adding a mapper.
 * All varga math runs on SIDEREAL longitudes.
 */

export type VargaMapper = (lonSidereal: number) => number; // returns sign 0-11

/** D1: the rashi itself. */
export const d1Sign: VargaMapper = (lon) => Math.floor(norm360(lon) / 30) % 12;

/**
 * D9 (Navamsa): 3°20' divisions counted continuously from Aries.
 * Equivalent to the classical fire→Aries / earth→Capricorn / air→Libra /
 * water→Cancer starting rule.
 */
export const d9Sign: VargaMapper = (lon) => Math.floor(norm360(lon) / (30 / 9)) % 12;

/**
 * D10 (Dasamsa): 3° divisions. Odd signs count from the sign itself,
 * even signs from the 9th sign from it.
 */
export const d10Sign: VargaMapper = (lon) => {
  const l = norm360(lon);
  const sign = Math.floor(l / 30) % 12;
  const part = Math.floor((l - sign * 30) / 3);
  const isOdd = sign % 2 === 0; // Aries (index 0) is the 1st = odd sign
  const start = isOdd ? sign : (sign + 8) % 12;
  return (start + part) % 12;
};

export const VARGA_MAPPERS: Record<VargaId, { mapper: VargaMapper; description: string }> = {
  D1: { mapper: d1Sign, description: 'Rashi — the kundli itself' },
  D9: { mapper: d9Sign, description: 'Navamsa — marriage, dharma, inner strength' },
  D10: { mapper: d10Sign, description: 'Dasamsa — career and public life' },
};

export function computeVarga(
  varga: VargaId,
  positions: PlanetPosition[],
  lagnaLonSidereal?: number,
): VargaChart {
  const { mapper, description } = VARGA_MAPPERS[varga];
  const placements: VargaPlacement[] = positions.map((p) => {
    const sign = mapper(p.lonSidereal);
    return { body: p.planet, sign, signName: signName(sign), rashiName: rashiName(sign) };
  });
  if (lagnaLonSidereal !== undefined) {
    const sign = mapper(lagnaLonSidereal);
    placements.unshift({ body: 'lagna', sign, signName: signName(sign), rashiName: rashiName(sign) });
  }
  return { varga, description, placements };
}

export function computeStandardVargas(
  positions: PlanetPosition[],
  lagnaLonSidereal?: number,
): VargaChart[] {
  return (['D1', 'D9', 'D10'] as VargaId[]).map((v) =>
    computeVarga(v, positions, lagnaLonSidereal),
  );
}
